import mixpanel from "mixpanel-browser";

// Same Mixpanel project as the Word Add-in (globalbit-addin-frontend, see its
// src/providers/mixpanel.ts) — so this web platform's usage shows up
// alongside the add-in's in the same account-health data. Deliberately
// minimal: this only establishes presence (web_app_opened + officeId) so
// customers who work through the web platform aren't misread as inactive by
// the daily usage digest, which previously had zero visibility into this
// surface. Per-feature event tracking beyond presence is being added
// incrementally — PDF extraction (ocr_extraction_*) is the first; Cases/
// Recordings/Legal Assistant completions on the web are still a follow-up.
const PRODUCTION_TOKEN = "09c3dac22c80c98dab14f34d1a8c3a22";
const IS_PRODUCTION = import.meta.env.VITE_SENTRY_ENVIRONMENT === "production";
const LOCAL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
const LOCAL_ENABLED = import.meta.env.VITE_MIXPANEL_ENABLED === "true";
const API_HOST =
  (import.meta.env.VITE_MIXPANEL_API_HOST as string | undefined) ||
  "https://api-eu.mixpanel.com";
const TEAM_DOMAINS = ["@legalix.io", "@legalix.ai", "@globalbit.co.il"];

function mixpanelToken(): string {
  return LOCAL_TOKEN?.trim() || PRODUCTION_TOKEN;
}

function isTrackingEnabled(): boolean {
  if (LOCAL_ENABLED && LOCAL_TOKEN?.trim()) return true;
  return IS_PRODUCTION;
}

function isTeamMember(email?: string | null): boolean {
  if (!email) return false;
  return TEAM_DOMAINS.some((domain) => email.endsWith(domain));
}

const INITIAL_TOUCH_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
  "gclid",
] as const;
const INITIAL_TOUCH_STORAGE = "legalix_initial_touch";

function captureInitialTouch(): void {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem(INITIAL_TOUCH_STORAGE)) return;
    const query = new URLSearchParams(window.location.search);
    const touch: Record<string, string> = {};
    for (const key of INITIAL_TOUCH_KEYS) {
      const value = query.get(key)?.trim();
      if (value) touch[key] = value.slice(0, 200);
    }
    if (Object.keys(touch).length === 0) return;
    sessionStorage.setItem(INITIAL_TOUCH_STORAGE, JSON.stringify(touch));
  } catch {
    // sessionStorage can throw in private mode; the URL params are then lost.
  }
}

function applyInitialTouch(): void {
  try {
    const raw = sessionStorage.getItem(INITIAL_TOUCH_STORAGE);
    if (!raw) return;
    const touch = JSON.parse(raw) as Record<string, unknown>;
    const setOnce: Record<string, string> = {};
    for (const [key, value] of Object.entries(touch)) {
      if (typeof value === "string" && value) setOnce[`initial_${key}`] = value;
    }
    if (Object.keys(setOnce).length === 0) return;
    mixpanel.people.set_once(setOnce);
  } catch {
    // A bad stash must not block identify.
  }
}

export function initMixpanel(): void {
  captureInitialTouch();
  const token = mixpanelToken();

  if (!isTrackingEnabled()) {
    mixpanel.init(token, { opt_out_tracking_by_default: true });
    console.log("[Mixpanel] Initialized in dev mode (tracking disabled)");
    return;
  }

  mixpanel.init(token, {
    api_host: API_HOST,
    // Local only: previous opt_out runs persist in localStorage, and DNT
    // silently drops events — both look like "no Mixpanel connection".
    debug: !IS_PRODUCTION,
    ignore_dnt: !IS_PRODUCTION,
  });
  if (!IS_PRODUCTION) {
    mixpanel.clear_opt_in_out_tracking();
    mixpanel.opt_in_tracking();
    mixpanel.track("sdk_initialized", { platform: "web" });
  }
  console.log(
    `[Mixpanel] Tracking enabled (${IS_PRODUCTION ? "production" : "local"}) host=${API_HOST}`,
  );
}

export function identifyMixpanelUser(uid: string, email?: string | null, officeId?: string): void {
  if (!isTrackingEnabled()) return;

  mixpanel.identify(uid);
  applyInitialTouch();
  mixpanel.register({
    platform: "web",
    is_team_member: isTeamMember(email),
    // Auto-attaches to every event tracked from here on, same purpose as
    // the Word Add-in's identical super-property.
    officeId,
  });
}

export function trackWebAppOpened(officeId?: string): void {
  if (!isTrackingEnabled()) return;
  mixpanel.track("web_app_opened", { officeId });
}

/**
 * PDF extraction (OCR) on the web platform.
 *
 * INSIGHT ONLY — deliberately NOT a counting source for the daily usage
 * digest. Web extractions run through the Nest case-file pipeline and land in
 * Postgres `case_file_pages`, which the digest's OCR module already counts via
 * `account-health-snapshot`. Adding these to `MODULE_EVENTS` would count every
 * web extraction twice. What they add instead is what Postgres cannot answer:
 * which interface ran it, how long users waited, how often a re-upload was
 * served from a previous extraction, and where extractions fail.
 *
 * `officeId` is already a registered super-property; it is passed explicitly
 * here for parity with web_app_opened and because historical officeId coverage
 * on client-side events has been sparse.
 */
export function trackOcrExtractionStarted(props: {
  officeId?: string;
  pageCount: number;
  fileSizeBytes: number;
  isScanned: boolean;
}): void {
  if (!isTrackingEnabled()) return;
  mixpanel.track("ocr_extraction_started", props);
}

export function trackOcrExtractionCompleted(props: {
  officeId?: string;
  pageCount: number;
  pagesTotal?: number;
  pagesFailed?: number;
  durationMs: number;
  /** True when a re-upload was served from a previous extraction, with no new OCR. */
  reused: boolean;
  creditsConsumed: number;
}): void {
  if (!isTrackingEnabled()) return;
  mixpanel.track("ocr_extraction_completed", props);
}

/**
 * A user re-uploaded a file this office has already extracted. Fired at the
 * moment reuse is detected — before the cached Markdown is fetched — so an
 * attempt is recorded even if that fetch then fails.
 *
 * No file name is sent: document names here routinely contain client and
 * counterparty names.
 */
export function trackOcrReextractionAttempted(props: {
  officeId?: string;
  pageCount?: number;
  fileSizeBytes?: number;
  /** Page count of the extraction being served back, when the backend reports it. */
  existingPagesTotal?: number;
}): void {
  if (!isTrackingEnabled()) return;
  mixpanel.track("ocr_reextraction_attempted", props);
}

export function trackOcrExtractionFailed(props: {
  officeId?: string;
  pageCount?: number;
  durationMs: number;
  /** OcrError code, or a local reason such as INSUFFICIENT_CREDITS. */
  errorCode: string;
}): void {
  if (!isTrackingEnabled()) return;
  mixpanel.track("ocr_extraction_failed", props);
}

export function trackOcrExtractionCancelled(props: {
  officeId?: string;
  pageCount?: number;
  durationMs: number;
}): void {
  if (!isTrackingEnabled()) return;
  mixpanel.track("ocr_extraction_cancelled", props);
}

export type WallShownProps = {
  wall?: string;
  app?: string;
  required?: number;
  available?: number;
  window_ends_at?: string | null;
  surface: "web";
  feature?: string;
  ui: "modal" | "dialog" | "toast" | "generic_error" | "tabs_hidden" | "no_access" | "feature_preview";
  message_key?: string;
};

export function trackWallShown(props: WallShownProps): void {
  if (!IS_PRODUCTION) return;
  mixpanel.track("wall_shown", props);
}

export function trackWallCta(props: {
  cta: "site" | "email" | "phone" | "whatsapp";
  wall?: string;
  surface: "web";
  ui: "dialog" | "modal" | "feature_preview";
}): void {
  if (!IS_PRODUCTION) return;
  mixpanel.track("wall_cta_clicked", props);
}

export { mixpanel };
